import { Check, X, Spinner } from "@phosphor-icons/react";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

interface FolderInlineEditProps {
  folderId: string;
  folderName: string;
  onSave: (name: string) => Promise<void>;
  isPending: boolean;
  onCancel: () => void;
}

export function FolderInlineEdit({
  folderId,
  folderName,
  onSave,
  isPending,
  onCancel,
}: FolderInlineEditProps) {
  const { t } = useTranslation("ui");
  const [editValue, setEditValue] = useState(folderName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // why: blur can run before this render's isSubmitting is true, and cancelling then unmounts the form so the rename never saves.
  const submittingRef = useRef(false);
  const blurCancelTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (blurCancelTimer.current != null) {
        window.clearTimeout(blurCancelTimer.current);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    submittingRef.current = true;
    if (!editValue.trim() || editValue === folderName) {
      submittingRef.current = false;
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave(editValue);
      // why: the ref must mean "a save is in flight", not "this instance ever
      // submitted" — clear it before onCancel so blur-cancel still works if the
      // caller keeps this form mounted across the save.
      submittingRef.current = false;
      // Close the edit form on successful save
      onCancel();
    } catch (err) {
      console.error("Failed to rename folder:", err);
      // why: a blur may have armed the 0ms cancel before this submit. The
      // rejection settles as a microtask, so that timer is still pending and
      // would re-read the ref below as false and close the editor, dropping
      // the typed name. Clear it before restoring the retry state.
      if (blurCancelTimer.current != null) {
        window.clearTimeout(blurCancelTimer.current);
        blurCancelTimer.current = null;
      }
      // Keep editing state active so user can retry
      submittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the new focus target is within the same form
    const relatedTarget = e.relatedTarget as HTMLElement;
    const formElement = e.currentTarget.closest("form");

    // Don't close if the new focus target is within the same form
    if (relatedTarget && formElement && formElement.contains(relatedTarget)) {
      return;
    }

    if (isPending || isSubmitting || submittingRef.current) {
      return;
    }

    // why: Enter and requestSubmit blur with relatedTarget null before submit; cancelling in that turn drops the rename.
    if (blurCancelTimer.current != null) {
      window.clearTimeout(blurCancelTimer.current);
    }
    blurCancelTimer.current = window.setTimeout(() => {
      blurCancelTimer.current = null;
      if (!submittingRef.current) {
        onCancel();
      }
    }, 0);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex min-w-0 items-center"
      data-testid={`breadcrumb-edit-form-${folderId}`}
    >
      <input
        ref={inputRef}
        className="puntt-link bg-white font-inherit m-0 w-[180px] min-w-0 overflow-hidden rounded border border-puntt-blue-9 p-1 text-[20px] leading-[28px] tracking-[-0.08px] shadow-sm outline-none focus:ring-0"
        value={editValue}
        autoFocus
        onFocus={(e) => e.target.select()}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-testid={`breadcrumb-edit-input-${folderId}`}
        disabled={isPending}
      />
      <button
        type="submit"
        className="hover:bg-green-50 ml-1 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={t("tickets.folderInlineEdit.saveFolderName")}
        tabIndex={-1}
        disabled={
          !editValue.trim() ||
          editValue === folderName ||
          isPending ||
          isSubmitting
        }
        onMouseDown={(e) => {
          // Prevent the input from losing focus when clicking the submit button
          // Only prevent default if the button is enabled (not disabled)
          const isDisabled =
            !editValue.trim() ||
            editValue === folderName ||
            isPending ||
            isSubmitting;
          if (!isDisabled) {
            e.preventDefault();
          }
        }}
        data-testid={`breadcrumb-save-button-${folderId}`}
      >
        {isPending || isSubmitting ? (
          <Spinner size="1" />
        ) : (
          <Check size={18} className="text-green-600" />
        )}
      </button>
      <button
        type="button"
        className="hover:bg-red-50 ml-1 flex cursor-pointer items-center justify-center rounded border-none bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={t("tickets.folderInlineEdit.cancelFolderNameEdit")}
        onClick={onCancel}
        tabIndex={-1}
        disabled={isPending || isSubmitting}
        data-testid={`breadcrumb-cancel-button-${folderId}`}
      >
        <X size={18} className="text-red-600" />
      </button>
    </form>
  );
}
